import React from 'react';
import { get, minBy } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import {
  VehicleModel,
  buildVehiclePath,
  buildPopularStylesPath,
  buildMakeModelDefaultYear,
} from 'client/data/models/vehicle-v2';
import { IncentiveModel, getIncentivesPath } from 'client/data/models/incentives';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import {
  buildCoreConfigurationsPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getModelState } from 'client/data/luckdragon/model';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { handle404Error } from 'client/site-modules/shared/utils/errors';
import { setCriticalCss } from 'client/actions/critical-css';
import { updateVehicle, buildContextVehicleFromMakeModelSubmodelYear } from 'client/actions/vehicle';
import { getQuery } from 'client/utils/location';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getCurrentMonth, getCurrentYear } from 'site-modules/shared/utils/time-util';
import { PageModel } from 'client/data/models/page';
import { setAdsListToPreload } from 'client/site-modules/shared/components/native-ad/utils/utils';
import { BUILD_PRICE_AD, INCENTIVES_AD } from 'client/site-modules/shared/components/native-ad/utils/constants';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { IncentivesModel, PAGE_DISPLAY_NAME, URL_PATH } from './incentives-model';

export const pageDefinitionConfiguration = params => ({
  name: 'car_incentives_model',
  category: 'car_incentives',
  preload: async (store, props) => {
    const { defaultStyleId } = getQuery({ search: props.location.search });
    const preloader = new ModelPreloader(store);
    const makeModelSubmodelYearPath = buildVehiclePath(params);
    let modelState;

    preloader.resolve(makeModelSubmodelYearPath, VehicleModel);
    preloader.resolve(buildMakeModelDefaultYear(params), VehicleModel);

    modelState = await preloader.load();

    const makeModelSubmodelYear = modelState.get(makeModelSubmodelYearPath, VehicleModel);
    const vehicleParams = getParamsFromVehicle(makeModelSubmodelYear);
    if (!makeModelSubmodelYear) {
      throw handle404Error({ errorMessage: `VehicleValidatorError - Could not find ${makeModelSubmodelYearPath}` });
    }

    const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);

    if (!isUsed) {
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
    }

    const vehicleForContext = buildContextVehicleFromMakeModelSubmodelYear(makeModelSubmodelYear);
    store.dispatch(updateVehicle(vehicleForContext));

    // this must happen after store.dispatch(updateVehicle(vehicleForContext)) so that pageContext.vehicle has been set
    const vehiclePageContext = modelState.get('vehicle', PageModel);
    setAdsListToPreload(
      preloader,
      store.getState(),
      [INCENTIVES_AD.AD_NAME, BUILD_PRICE_AD.AD_NAME],
      vehiclePageContext
    );

    const popularStylePath = buildPopularStylesPath(params);
    preloader.resolve(popularStylePath, VehicleModel);

    if (!isUsed) {
      preloader.resolve(buildCoreConfigurationsPath(vehicleParams), InventoryCoreConfigurationsModel);
    }

    modelState = await preloader.load();

    const popularStyles = modelState.get(popularStylePath, VehicleModel);

    const styleId = get(
      popularStyles?.find?.(({ id }) => parseInt(defaultStyleId, 10) === id) ||
        minBy(popularStyles, 'price.baseMSRP') ||
        get(popularStyles, '[0]'),
      'id'
    );

    preloader.resolve(getIncentivesPath(styleId), IncentiveModel);

    const coreConfigurations = modelState.get(
      buildCoreConfigurationsPath(vehicleParams),
      InventoryCoreConfigurationsModel
    );
    if (
      !isUsed &&
      (!get(coreConfigurations, 'length') ||
        !get(
          coreConfigurations.find(({ id }) => parseInt(styleId, 10) === parseInt(id, 10)),
          'inventoryCount'
        ))
    ) {
      preloader.resolve(
        getUnifiedLeadFormDealerCountV2Path({
          makeSlug: makeModelSubmodelYear.make.slug,
          modelSlug: makeModelSubmodelYear.model.slug,
          strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
        }),
        UnifiedLeadFormModel
      );
    }

    modelState = await preloader.load();

    const incentives = modelState.get(getIncentivesPath(styleId), IncentiveModel);

    store.dispatch(
      setCriticalCss({
        hasIncentives: !!get(incentives, 'numIncentives'),
      })
    );
  },
  seo: state => {
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);
    const makeName = get(makeModelSubmodelYear, 'make.name', '');
    const modelName = get(makeModelSubmodelYear, 'model.name', '');
    const year = get(makeModelSubmodelYear, 'year', '');
    const breadcrumbs = buildCoreBreadcrumbs({
      makeModelSubmodelYear,
      params,
      defaultYear,
      pageDisplayName: PAGE_DISPLAY_NAME,
      urlPath: getUrlPathFromParams(params, URL_PATH),
      includeLastLink: true,
    });

    return {
      title: `${year} ${makeName} ${modelName} Deals, Incentives & Rebates for ${getCurrentMonth()} ${getCurrentYear()} | Edmunds`,
      description: `Browse the latest ${year} ${makeName} ${modelName} deals, incentives, and rebates in your area at Edmunds.com. Find the best ${makeName} ${modelName} discounts and current offers.`,
      keywords: `${makeName} ${modelName} Incentives and Rebates`,
      jsonld: [getBreadcrumbSchema(breadcrumbs)],
    };
  },
  component: () => <IncentivesModel params={params} />,
  options: {
    hasVehicleContext: true,
  },
});

export default params => pageDefinition(pageDefinitionConfiguration(params));
